import type { AppDispatch } from "_shared/_stores/store-types";
import {
  getFeeScheduleTeamMembers,
  withFeeScheduleStaffingSeated,
} from "_shared/fee-schedules/categories/fee-schedule-team-members";
import { staffingNotOnTeam } from "_shared/fee-schedules/categories/fee-schedule-template-staffing";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import type { NavigateFunction } from "react-router";
import type Session from "users/__session/session";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import { Masked } from "work/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { PageTab } from "work/proposals/_views/drafts/draft-proposal-page-tabs";
import { openUnsavedDraft } from "work/proposals/_views/drafts/open-unsaved-draft";
import TeamTemplate from "work/team-templates/team-template";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

type StartDraftProposalFromTemplateArgs<TTemplate> = {
  dispatch: AppDispatch;
  navigate: NavigateFunction;
  session: Session;
  template: TTemplate;
};

export function startDraftProposalFromTeamTemplate(args: StartDraftProposalFromTemplateArgs<TeamTemplate>) {
  const { dispatch, navigate, session, template } = args;
  const selectedTeam = new VendorTeam({
    leader: template.leader,
    members: template.members,
    marketplaceId: template.marketplaceId,
  });
  let builder = new ProposalBuilder({ session }).setVendorTeam(selectedTeam);

  if (template.id) {
    builder = session.context?.viewingAsVendor
      ? builder.setVendorTeamTemplateIds([template.id])
      : builder.setClientTeamTemplateIds([template.id]);
  }

  openUnsavedDraft({
    builder,
    session,
    dispatch,
    navigate,
    pageTab: PageTab.VendorTeam,
  });
}

/**
 * Starts a draft from a fee schedule template, keeping who each rate is for.
 *
 * The other two surfaces that apply a template ask what to do about a person the proposal's team
 * does not have, because there they would be changing a team that the user built. Here the builder
 * is new, so nobody on its team can be displaced and there is nothing to trade away. The people the
 * template names who are not on it join it, and the message says so, which is what keeps it from
 * happening silently (#5237).
 *
 * "Not on it" is measured against the team the new builder already has. A vendor's new draft
 * already has the vendor as its leader (#5658): replacing that team with the template's people
 * took the leader away again, and counting the vendor as missing added them as a member of the
 * team they lead. Nobody else is picked as leader. A client's draft still starts with no vendor
 * leader, and the user picks one on the Vendor Team tab exactly as they did before.
 */
export function startDraftProposalFromFeeScheduleTemplate(
  args: StartDraftProposalFromTemplateArgs<FeeScheduleTemplate>,
) {
  const { dispatch, navigate, session, template } = args;
  let builder = new ProposalBuilder({ session }).setFeeSchedule([...template.categories]);

  const newDraftTeam = builder.currentSpec.workAgreement.vendorTeam;
  const seatedTeam = Masked.isPresentNotMasked(newDraftTeam) ? newDraftTeam : new VendorTeam({ members: [] });
  const staffing = staffingNotOnTeam(template.categories, getFeeScheduleTeamMembers(seatedTeam));
  if (staffing.length > 0) {
    builder = builder.setVendorTeam(withFeeScheduleStaffingSeated(seatedTeam, staffing));
    enqueueSnackbar(
      staffing.length === 1
        ? "Added the person this fee schedule names to the vendor team."
        : `Added the ${staffing.length} people this fee schedule names to the vendor team.`,
      { variant: "info" },
    );
  }

  if (template.id) {
    builder = session.context?.viewingAsVendor
      ? builder.setVendorFeeScheduleTemplateIds([template.id])
      : builder.setClientFeeScheduleTemplateIds([template.id]);
  }

  openUnsavedDraft({
    builder,
    session,
    dispatch,
    navigate,
    pageTab: PageTab.FeeSchedule,
  });
}
